import { useState, type FormEvent } from 'react'
import { BasculeMethode, ConnexionWhatsapp, memoriserMethode, methodeMemorisee, type MethodeConnexion } from '../api/commun'
import { supabase } from '../api/supabase'
import { Logo } from './Icone'
import { BoutonGoogle, connexionGoogle } from '../api/google'
import { messageCompteCree, premiereErreur, validerInscription, type ChampInscription } from '../api/compte'
import { MESSAGE_LIEN_ENVOYE, RETOUR_CONSOLE, demanderReinitialisation } from '../api/motDePasseOublie'
import { BasculeAcces, idOngletAcces, idPanneauAcces, useContenuAcces, type ModeAcces } from './BasculeAcces'

/* Liens-texte de la carte (pas de classe dédiée : on évite de toucher la feuille de style partagée). */
const STYLE_LIEN = {
  background: 'none', border: 'none', padding: 0, margin: '0 0 12px auto', display: 'block',
  color: '#0B6B52', fontSize: 12.5, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit',
} as const

const NOTE_COMPTE =
  'Créer un compte ne donne pas accès à la console : un administrateur SwiftDeliver doit vous donner accès.'

/**
 * Écran de connexion — TRANSPOSITION du gabarit d'origine.
 *
 * ⚠️ Deux champs étaient PRÉ-REMPLIS dans le HTML : l'e-mail
 * `operations@swiftdeliver.ml`, et surtout un mot de passe valant littéralement
 * `••••••••`. Ces puces n'étaient pas un masque d'affichage — c'était la VALEUR
 * du champ. Qui cliquait « Se connecter » sans rien saisir envoyait donc huit
 * puces comme mot de passe et recevait un refus incompréhensible. Les champs
 * partent vides.
 */
export function Connexion({
  surConnexion,
  surConnexionWhatsapp,
  surInscription,
  erreur,
  surErreur,
}: {
  surConnexion: (email: string, motDePasse: string) => Promise<void>
  /** Appelée une fois le code WhatsApp vérifié : même chemin d'accès que le mot de passe. */
  surConnexionWhatsapp: (userId: string) => Promise<void>
  /** Crée le compte ; `sessionOuverte` faux = confirmation par e-mail requise. */
  surInscription: (nom: string, email: string, motDePasse: string) => Promise<{ sessionOuverte: boolean }>
  erreur: string | null
  /** Pour que le bouton Google affiche son refus au même endroit que le mot de passe. */
  surErreur: (message: string | null) => void
}) {
  const [email, setEmail] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [nom, setNom] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [mode, setMode] = useState<ModeAcces>('connexion')
  const { contenu, sortant } = useContenuAcces(mode)
  /* Numéro + code WhatsApp (au Mali, plus courant que l'e-mail) ou e-mail : le dernier choix est mémorisé. */
  const [methode, setMethode] = useState<MethodeConnexion>(() => methodeMemorisee())
  /* Change après un refus d'accès pour remettre le formulaire WhatsApp à l'étape « numéro ». */
  const [cleWhatsapp, setCleWhatsapp] = useState(0)
  /* E-mail du compte tout juste créé (panneau de succès). */
  const [compteCree, setCompteCree] = useState<string | null>(null)
  const [champInvalide, setChampInvalide] = useState<ChampInscription | null>(null)
  /* Erreur propre à l'inscription (validation, Supabase) ; `erreur` vient du parent (accès refusé, Google…). */
  const [erreurLocale, setErreurLocale] = useState<string | null>(null)
  const message = erreurLocale ?? erreur
  /* « Mot de passe oublié ? » : même carte, autre formulaire. */
  const [oubli, setOubli] = useState(false)
  const [lienEnvoye, setLienEnvoye] = useState(false)

  function changerMode(m: ModeAcces) {
    if (m === mode) return
    setMode(m)
    setErreurLocale(null)
    setChampInvalide(null)
    setCompteCree(null)
    surErreur(null)
  }

  function changerMethode(m: MethodeConnexion) {
    if (m === methode) return
    setMethode(m)
    memoriserMethode(m)
    setErreurLocale(null)
    surErreur(null)
  }

  async function creer(e: FormEvent) {
    e.preventDefault()
    if (enCours) return
    const premiere = premiereErreur(validerInscription({ nom, email, motDePasse, confirmation }))
    if (premiere) {
      setErreurLocale(premiere.message)
      setChampInvalide(premiere.champ)
      document.getElementById(`ins-${premiere.champ}`)?.focus()
      return
    }
    setEnCours(true)
    setErreurLocale(null)
    setChampInvalide(null)
    surErreur(null)
    try {
      const r = await surInscription(nom.trim(), email.trim(), motDePasse)
      if (!r.sessionOuverte) {
        setCompteCree(email.trim())
        setMotDePasse('')
        setConfirmation('')
      }
    } catch (err) {
      setErreurLocale(err instanceof Error ? err.message : 'Création du compte impossible.')
    } finally {
      setEnCours(false)
    }
  }

  function ouvrirOubli() {
    setOubli(true)
    setLienEnvoye(false)
    setErreurLocale(null)
    surErreur(null)
  }

  function fermerOubli() {
    setOubli(false)
    setLienEnvoye(false)
    setErreurLocale(null)
  }

  async function demander(e: FormEvent) {
    e.preventDefault()
    if (enCours) return
    setEnCours(true)
    setErreurLocale(null)
    try {
      await demanderReinitialisation(email, RETOUR_CONSOLE)
      setLienEnvoye(true)
    } catch (err) {
      setErreurLocale(err instanceof Error ? err.message : 'Envoi impossible.')
    } finally {
      setEnCours(false)
    }
  }

  const invalide = (c: ChampInscription) => (champInvalide === c ? true : undefined)

  async function soumettre(e: FormEvent) {
    e.preventDefault()
    if (enCours) return
    setEnCours(true)
    try {
      await surConnexion(email.trim(), motDePasse)
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div id="login" style={{ display: 'flex' }}>
      <div className="login-card">
        <div className="login-logo">
          <span className="logo-badge logo-anim" id="logo-login">
            <Logo />
          </span>
          <div className="brand-wm">
            <span className="sw">Swift</span>
            <span className="dl">Deliver</span>
            <span className="ops-tag">Opérations</span>
          </div>
        </div>
        <p className="login-sub">{oubli ? 'Mot de passe oublié' : 'Console opérateur — offre Opérée (livraisons outsourcées)'}</p>
        <svg className="route-line" viewBox="0 0 352 8" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <line x1="4" y1="4" x2="348" y2="4" />
        </svg>

        {oubli ? (
          <form role="group" aria-label="Mot de passe oublié" onSubmit={demander}>
            {lienEnvoye ? (
              <p className="compte-cree" role="status">
                {MESSAGE_LIEN_ENVOYE}
              </p>
            ) : (
              <>
                <p className="note-acces">
                  Saisissez l’adresse e-mail de votre compte : nous vous envoyons un lien pour choisir un nouveau mot de passe.
                </p>
                <div className="field">
                  <label htmlFor="o-email">Adresse e-mail</label>
                  <input
                    id="o-email"
                    type="email"
                    autoComplete="email"
                    placeholder="vous@yiriwa-group.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </>
            )}
            {erreurLocale && (
              <p role="alert" style={{ color: 'var(--err)', fontSize: 12.5, margin: '0 0 12px', lineHeight: 1.45 }}>
                {erreurLocale}
              </p>
            )}
            {!lienEnvoye && (
              <button className="btn btn-gold" type="submit" disabled={enCours}>
                {enCours ? 'Envoi…' : 'Envoyer le lien'}
              </button>
            )}
            <button type="button" style={{ ...STYLE_LIEN, margin: '14px auto 0' }} onClick={fermerOubli}>
              Retour à la connexion
            </button>
          </form>
        ) : (
        <>
        <BasculeAcces mode={mode} surChangement={changerMode} desactive={enCours} />
        <div
          className={`bascule-contenu${sortant ? ' sortant' : ''}`}
          key={contenu}
          role="tabpanel"
          id={idPanneauAcces}
          aria-labelledby={idOngletAcces(contenu)}
        >
          {contenu === 'connexion' && (
            <div style={{ margin: '0 0 16px' }}>
              <BasculeMethode methode={methode} surChangement={changerMethode} desactive={enCours} />
            </div>
          )}
          {contenu === 'connexion' && methode === 'whatsapp' ? (
            <>
              <ConnexionWhatsapp
                key={cleWhatsapp}
                client={supabase}
                surConnecte={async (userId) => {
                  await surConnexionWhatsapp(userId)
                  setCleWhatsapp((k) => k + 1)
                }}
              />
              {message && (
                <p role="alert" style={{ color: 'var(--err)', fontSize: 12.5, margin: '12px 0 0', lineHeight: 1.45 }}>
                  {message}
                </p>
              )}
              <p className="note-acces" style={{ marginTop: 14 }}>
                Les comptes sont créés par l’équipe SwiftDeliver : utilisez le numéro qu’elle a enregistré pour vous.
              </p>
            </>
          ) : (
          <form onSubmit={contenu === 'connexion' ? soumettre : creer} noValidate={contenu === 'inscription'}>
          {contenu === 'connexion' && (
            <>
              <div className="field">
                <label htmlFor="l-email">Adresse e-mail</label>
                <input
                  id="l-email"
                  type="email"
                  autoComplete="username"
                  placeholder="vous@yiriwa-group.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="l-pass">Mot de passe</label>
                <input
                  id="l-pass"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Votre mot de passe"
                  value={motDePasse}
                  onChange={(e) => setMotDePasse(e.target.value)}
                  required
                />
              </div>
              <button type="button" style={STYLE_LIEN} onClick={ouvrirOubli} disabled={enCours}>
                Mot de passe oublié ?
              </button>
            </>
          )}
          {contenu === 'inscription' && compteCree === null && (
            <>
              <div className="field">
                <label htmlFor="ins-nom">Nom complet</label>
                <input
                  id="ins-nom"
                  type="text"
                  autoComplete="name"
                  placeholder="Prénom Nom"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  aria-invalid={invalide('nom')}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="ins-email">Adresse e-mail</label>
                <input
                  id="ins-email"
                  type="email"
                  autoComplete="email"
                  placeholder="vous@yiriwa-group.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={invalide('email')}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="ins-motDePasse">Mot de passe</label>
                <input
                  id="ins-motDePasse"
                  type="password"
                  autoComplete="new-password"
                  placeholder="8 caractères minimum"
                  minLength={8}
                  value={motDePasse}
                  onChange={(e) => setMotDePasse(e.target.value)}
                  aria-invalid={invalide('motDePasse')}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="ins-confirmation">Confirmer le mot de passe</label>
                <input
                  id="ins-confirmation"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Retapez le mot de passe"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  aria-invalid={invalide('confirmation')}
                  required
                />
              </div>
              <p className="note-acces">{NOTE_COMPTE}</p>
            </>
          )}
          {contenu === 'inscription' && compteCree !== null && (
            <>
              <p className="compte-cree" role="status">
                {messageCompteCree(compteCree)}
              </p>
              <p className="note-acces">{NOTE_COMPTE}</p>
            </>
          )}

          {message && (
            <p role="alert" style={{ color: 'var(--err)', fontSize: 12.5, margin: '0 0 12px', lineHeight: 1.45 }}>
              {message}
            </p>
          )}

          {contenu === 'connexion' && (
            <button className="btn btn-gold" type="submit" disabled={enCours}>
              {enCours ? 'Connexion…' : 'Se connecter'}
            </button>
          )}
          {contenu === 'inscription' && compteCree === null && (
            <button className="btn btn-gold" type="submit" disabled={enCours}>
              {enCours ? 'Création…' : 'Créer mon compte'}
            </button>
          )}
          {contenu === 'inscription' && compteCree !== null && (
            <button className="btn btn-gold" type="button" onClick={() => changerMode('connexion')}>
              Aller à la connexion
            </button>
          )}

          {contenu === 'connexion' && <BoutonGoogle connexion={connexionGoogle} surErreur={(m) => surErreur(m || null)} desactive={enCours} />}
          </form>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  )
}
